import { useState, useEffect } from 'react'

import { throttle } from 'lodash'

import { buildPassTheme } from 'src/styles/theme'

export type Breakpoint = keyof typeof buildPassTheme.breakpoints

const getDeviceConfig = (width: number, breakpoint: Breakpoint) => {
  return width < buildPassTheme.breakpoints[breakpoint]
}

/**
 * Checks whether the width of the window is less than the breakpoint provided
 * @param {Breakpoint} breakpoint
 * @returns {boolean} true | false
 */
export const useBreakpoint = (breakpoint: Breakpoint) => {
  const [brkPnt, setBrkPnt] = useState(false) // Prevent hydration mismatch

  useEffect(() => {
    setBrkPnt(getDeviceConfig(window.innerWidth, breakpoint))

    const calcInnerWidth = throttle(() => {
      setBrkPnt(getDeviceConfig(window.innerWidth, breakpoint))
    }, 200)

    window.addEventListener('resize', calcInnerWidth)

    return () => window.removeEventListener('resize', calcInnerWidth)
  }, [breakpoint])

  return brkPnt
}

const getCurrentBreakpoint = (width: number) => {
  if (width < buildPassTheme.breakpoints.md) {
    return 'sm'
  }
  if (width < buildPassTheme.breakpoints.lg) {
    return 'md'
  }
  if (width < buildPassTheme.breakpoints.xl) {
    return 'lg'
  }
  if (width < buildPassTheme.breakpoints.xxl) {
    return 'xl'
  }
  return 'xxl'
}

/**
 * Checks whether the width of the window is less than the breakpoint provided
 * @param {Breakpoint} breakpoint
 * @returns {boolean} true | false
 */
export const useCurrentBreakpoint = () => {
  const [brkPnt, setBrkPnt] = useState(() =>
    getCurrentBreakpoint(typeof window !== 'undefined' ? window.innerWidth : 0)
  )

  useEffect(() => {
    const calcInnerWidth = throttle(() => {
      setBrkPnt(getCurrentBreakpoint(window.innerWidth))
    }, 200)

    window.addEventListener('resize', calcInnerWidth)

    return () => window.removeEventListener('resize', calcInnerWidth)
  }, [])

  return brkPnt
}
